import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./DeploymentSummary.css";
import { useLocation, useNavigate } from "react-router-dom";
import { formatToBrowserTime } from "../../../utils/timeFormat";
import { toast } from "react-toastify";
import { creatDeployment } from "../../../services/scm/Deployments";
import dayjs from "dayjs";

export default function DeploymentSummary({ title }) {
    const { t } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();

    // Destructure state coming from previous step
    const {
        packageName,
        versionName,
        deploymentName,
        description,
        jobType,
        retries,
        startMode,
        endMode,
        startTime,
        endTime,
        targets = [],              // thing names (for SNAPSHOT display)
        targetsArn = [],           // thing ARNs
        dependencyGroupsArn = [],  // group ARNs
        dependencyGroupNames = []  // group names (for CONTINUOUS display)
    } = state || {};

    const isSnapshot = jobType === "SNAPSHOT";
    const targetsCount = Array.isArray(targets) ? targets.length : 0;
    const dependencyGroupCount = Array.isArray(dependencyGroupNames)
        ? dependencyGroupNames.length
        : 0;

    // Helpers to navigate back to edit screens
    const goEditDeploymentSettings = () => {
        navigate("/software-management/create-deployment", {
            state: {
                packageName,
                versionName,
                deploymentName,
                description,
                jobType,
                retries,
                startMode,
                endMode,
                startTime,
                endTime,
                targets,
                targetsArn,
                dependencyGroupsArn,
                dependencyGroupNames
            },
        });
    };

    const goEditTargets = () => {
        navigate("/software-management/select-targets", {
            state: {
                packageName,
                versionName,
                deploymentName,
                description,
                jobType,
                retries,
                startMode,
                endMode,
                startTime,
                endTime,
                targets,
                targetsArn,
                dependencyGroupsArn,
                dependencyGroupNames
            },
        });
    };

    // Handle publish deployment
    const handlePublishDeployment = async () => {
        const targetsToSend =
            jobType === "SNAPSHOT"
                ? (Array.isArray(targetsArn) ? targetsArn : [])
                : (Array.isArray(dependencyGroupsArn) ? dependencyGroupsArn : []);

        const payload = {
            packageName,
            versionName,
            deploymentName,
            jobRunType: jobType,
            retries,
            targets: targetsToSend
        };

        if (startMode === "SCHEDULE" && startTime) {
            payload.startTime = dayjs(startTime).format("YYYY-MM-DDTHH:mm");
        }

        if (endMode === "HAS_END" && endTime) {
            payload.endTime = dayjs(endTime).format("YYYY-MM-DDTHH:mm");
        }

        try {
            await creatDeployment(payload);
            toast.success(
                `${deploymentName} for ${packageName}:${versionName} created successfully`
            );
            navigate("/software-management/track-deployment");
        } catch (err) {
            console.error("Create Deployment Failed", err);
            toast.error(
                `Failed to create deployment - ${deploymentName},\nPlease try again`
            );
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    // Redirect back if required data missing
    useEffect(() => {
        if (!packageName || !versionName || !deploymentName) {
            navigate("/software-management/deployments");
        }
    }, [packageName, versionName, deploymentName, navigate]);

    // Pretty labels for modes & times
    const getStartModeLabel = () => {
        if (startMode === "IMMEDIATELY") return t("Immediate");
        if (startMode === "SCHEDULE") return t("Scheduled");
        return "--";
    };

    const getEndModeLabel = () => {
        if (endMode === "NO_END") return t("No End");
        if (endMode === "HAS_END") return t("Has End Time");
        return "--";
    };

    const getStartTimeDisplay = () => {
        if (startMode === "SCHEDULE" && startTime) {
            return formatToBrowserTime(startTime);
        }
        return "--";
    };

    const getEndTimeDisplay = () => {
        if (endMode === "HAS_END" && endTime) {
            return formatToBrowserTime(endTime);
        }
        return "--";
    };

    return (
        <div className="container my-3">
            <div className="card-body col-12 col-xxl-10 responsive-col">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">
                        {t(title || "Deployment Summary")}
                    </h5>
                </div>

                {/* Deployment Settings Section */}
                <div className="card mt-3">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="card-subtitle mb-0">
                                {t("Deployment Settings")}
                            </h6>
                            <button
                                className="btn btn-transperant"
                                type="button"
                                onClick={goEditDeploymentSettings}
                            >
                                {t("Edit")}
                            </button>
                        </div>

                        <div className="row g-2">
                            {/* Package Name */}
                            <div className="col-6 col-md-3 fw-semibold">
                                {t("Package Name")}:
                            </div>
                            <div className="col-6 col-md-3 text-truncate" title={packageName}>
                                {packageName || "--"}
                            </div>

                            {/* Package Version */}
                            <div className="col-6 col-md-3 fw-semibold">
                                {t("Package Version")}:
                            </div>
                            <div className="col-6 col-md-3">
                                {versionName || "--"}
                            </div>

                            {/* Deployment Name */}
                            <div className="col-6 col-md-3 fw-semibold">
                                {t("Deployment Name")}:
                            </div>
                            <div className="col-6 col-md-9 text-truncate" title={deploymentName}>
                                {deploymentName || "--"}
                            </div>

                            {/* Description */}
                            <div className="col-6 col-md-3 fw-semibold">
                                {t("Description")}:
                            </div>
                            <div
                                className="col-6 col-md-9 description-text"
                                title={description}
                            >
                                {description || "--"}
                            </div>

                            {/* Job Type */}
                            <div className="col-6 col-md-3 fw-semibold">
                                {t("Job Type")}:
                            </div>
                            <div className="col-6 col-md-3">
                                {jobType || "--"}
                            </div>

                            {/* Retries */}
                            <div className="col-6 col-md-3 fw-semibold">
                                {t("Retries")}:
                            </div>
                            <div className="col-6 col-md-3">
                                {typeof retries === "number" ? retries : "--"}
                            </div>

                            {/* Start Mode */}
                            <div className="col-6 col-md-3 fw-semibold">
                                {t("Start Mode")}:
                            </div>
                            <div className="col-6 col-md-3">
                                {getStartModeLabel()}
                            </div>

                            {/* Start Time */}
                            <div className="col-6 col-md-3 fw-semibold">
                                {t("Start Time")}:
                            </div>
                            <div className="col-6 col-md-3">
                                {getStartTimeDisplay()}
                            </div>

                            {/* End Mode */}
                            <div className="col-6 col-md-3 fw-semibold">
                                {t("End Mode")}:
                            </div>
                            <div className="col-6 col-md-3">
                                {getEndModeLabel()}
                            </div>

                            {/* End Time */}
                            <div className="col-6 col-md-3 fw-semibold">
                                {t("End Time")}:
                            </div>
                            <div className="col-6 col-md-3">
                                {getEndTimeDisplay()}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Targets / Target Things / Target Groups Section */}
                <div className="card mt-3">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="card-subtitle mb-0">
                                {isSnapshot
                                    ? t("Target Things ({{count}})", { count: targetsCount })
                                    : t("Target Groups")}
                            </h6>
                            <button
                                type="button"
                                className="btn btn-transperant"
                                onClick={goEditTargets}
                            >
                                {t("Edit")}
                            </button>
                        </div>

                        <div className="row g-2">
                            <div className="col-12 col-md-9">
                                <div className="border rounded p-2 bg-light">
                                    {isSnapshot ? (
                                        // SNAPSHOT: show explicit target things + count
                                        targetsCount > 0 ? (
                                            targets.map((tgt, index) => {
                                                const label =
                                                    typeof tgt === "string"
                                                        ? tgt
                                                        : tgt.name || JSON.stringify(tgt);
                                                return (
                                                    <div
                                                        key={index}
                                                        className="pt-1 text-truncate"
                                                        title={label}
                                                    >
                                                        {label}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div>{t("No Targets Selected")}</div>
                                        )
                                    ) : (
                                        // CONTINUOUS: show target group names
                                        <>
                                            {dependencyGroupCount > 0 ? (
                                                dependencyGroupNames.map((groupName, index) => (
                                                    <div
                                                        key={index}
                                                        className="pt-1 text-truncate"
                                                        title={groupName}
                                                    >
                                                        {groupName}
                                                    </div>
                                                ))
                                            ) : (
                                                <div>{t("No Target Groups Selected")}</div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action buttons */}
                <div className="mt-4 d-flex justify-content-end gap-2">
                    <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={handleCancel}
                    >
                        {t("Cancel")}
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={handlePublishDeployment}
                    >
                        {t("Create")}
                    </button>
                </div>
            </div>
        </div>
    );
}
